import { ReactNode } from 'react';

import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { Document } from '@contentful/rich-text-types';
import { globalObservability } from '@opendoor/observability/slim';

import { MegaNavItem } from '../components/landing-pages-v2/shared/types';
import {
  IArticle,
  IArticleFields,
  ILandingPageV2,
  ILandingPageV2Fields,
  ILpComponentGetCashOfferModalFields,
  ILpElementLink,
  ILpElementSimpleList,
  IOpenGraphFields,
  ISeoBaseFields,
  ISiteFooter,
  ISiteHeader,
  ITopicPageFields,
} from '../declarations/contentful';
import { createContentfulClient, fetchEntriesById, fetchEntryById } from './api';
import articleEntryLookup from './entries/articleV2';
import landingPageV2EntryLookup from './entries/landingPageV2';
import topicPageEntryLookup from './entries/topicPageV2';
import { hydrateEntries, IIdToLoaderData } from './loaders';

export type ArticleProps = {
  sys: { createdAt: string; updatedAt: string; tags: Array<string> };
  fields: Omit<IArticleFields, 'keyTakeaways'> & {
    keyTakeaways?: Document | null;
  };
  readingTimeMinutes: number | null;
  idToLoaderData: IIdToLoaderData;
};

export type TopicPageProps = {
  sys: {
    createdAt: string;
    updatedAt: string;
    locale: string;
  };
  fields: ITopicPageFields;
  idToLoaderData: IIdToLoaderData;
};

export type LandingPageV2Props = {
  children?: ReactNode;
  fields: ILandingPageV2Fields;
  idToLoaderData: IIdToLoaderData;
  markets: [];
  public: boolean;
  showModal?: boolean;
  modalFields?: ILpComponentGetCashOfferModalFields | null;
};

export interface ILandingPageV2LayoutFields extends Partial<ILpComponentGetCashOfferModalFields> {
  /** Slug */
  slug: string;

  /** Header */
  header: ISiteHeader;

  /** Footer */
  footer?: ISiteFooter | undefined;

  /** SEO Base */
  seoBase?: ISeoBaseFields | undefined;

  /** Show Return Experience */
  showReturnExperienceBanner?: boolean | undefined;

  /** Extra Legal Text */
  extraLegalText?: Document | undefined;

  /** Show Eligibility Legal */
  showEligibilityLegal?: boolean | undefined;

  /** Show Customer Testimonial Legal */
  showCustomerTestimonialLegal?: boolean | undefined;

  /** Show the PDP Copyright Legal */
  showThePdpCopyrightLegal?: boolean | undefined;

  /** Show Promotion Legal */
  showPromotionLegal?: boolean | undefined;

  /** Open Graph Fields */
  openGraph?: IOpenGraphFields;

  /** Show Language Toggle */
  showLanguageToggle?: boolean | undefined;

  /** Should Show Trustpilot Reviews */
  shouldShowTrustpilotReviews?: boolean | undefined;
}

export type ILandingPageV2LayoutProps<T> = {
  children?: ReactNode;
  fields: T;
  idToLoaderData: IIdToLoaderData;
  hideGoogleOneTap?: boolean;
  showModal?: boolean;
  modalFields?: ILpComponentGetCashOfferModalFields | null;
};

/**
 * Fetch depth required to support MegaNav structure:
 * Page → header → mainNav → sections → links
 */
const FETCH_DEEP = 4;
const FETCH_SHALLOW = 2;

const sysFields = ['createdAt', 'updatedAt'];
const metaFields = ['tags'];
const selectedFields: Array<keyof IArticleFields> = [
  'slug',
  'seoBase',
  'authors',
  'showAuthorFooter',
  'articleName',
  'articleSubtitle',
  'header',
  'footer',
  // no relatedArticles
  'headerSummary',
  'headerImage',
  'keyTakeaways',
  'body',
  'editors',
  'reviewers',
  'openGraph',
  'openGraphArticle',
  'twitterCard',
  'publisher',
  'publishDate',
  'actualizationDate',
  'hideReadingTime',
];

type FetchBySlug = {
  slug: string;
  requestUrl: string;
  pageNum?: number;
  locale?: string;
};

async function fetchArticleMetadata(
  client: ReturnType<typeof createContentfulClient>,
  slug: string,
) {
  const fieldsWithoutBody = selectedFields.filter((f) => f !== 'body' && f !== 'keyTakeaways');

  const entry = await client.getEntries<
    Omit<IArticleFields, 'relatedArticles' | 'body' | 'keyTakeaways'>
  >({
    content_type: 'article',
    include: FETCH_DEEP,
    'fields.slug': slug,
    select: sysFields
      .map((x) => `sys.${x}`)
      .concat(fieldsWithoutBody.map((x) => `fields.${x}`))
      .concat(metaFields.map((x) => `metadata.${x}`))
      .join(','),
    limit: 1,
  });

  return entry.items.length > 0 ? entry : null;
}

async function fetchArticleBody(client: ReturnType<typeof createContentfulClient>, slug: string) {
  const bodyEntry = await client.getEntries<Pick<IArticleFields, 'body' | 'keyTakeaways'>>({
    content_type: 'article',
    include: FETCH_SHALLOW,
    'fields.slug': slug,
    select: 'fields.body,fields.keyTakeaways',
    limit: 1,
  });

  return bodyEntry.items.length > 0 ? bodyEntry : null;
}

function mergeArticleData(
  metadataEntry: Awaited<ReturnType<typeof fetchArticleMetadata>>,
  bodyEntry: Awaited<ReturnType<typeof fetchArticleBody>>,
): { article: IArticle; mergedIncludes: Array<any> } {
  if (!metadataEntry || !bodyEntry) {
    throw new Error('Cannot merge article data: missing metadata or body');
  }

  const safeMetadata = JSON.parse(metadataEntry.stringifySafe()) as Omit<
    typeof metadataEntry,
    'toPlainObject' | 'stringifySafe'
  >;
  const safeBody = JSON.parse(bodyEntry.stringifySafe()) as Omit<
    typeof bodyEntry,
    'toPlainObject' | 'stringifySafe'
  >;

  const article = safeMetadata.items[0] as IArticle;
  article.fields.body = safeBody.items[0].fields.body;
  article.fields.keyTakeaways = safeBody.items[0].fields.keyTakeaways;

  const mergedIncludes = [
    ...(safeMetadata.includes?.Entry ?? []),
    ...(safeBody.includes?.Entry ?? []),
  ];

  return { article, mergedIncludes };
}

async function hydrateArticleEntries(
  article: IArticle,
  mergedIncludes: Array<any>,
  requestUrl: string,
  slug: string,
): Promise<Record<string, any>> {
  try {
    const hydratedEntries = await Promise.all(
      hydrateEntries({
        includes: mergedIncludes,
        entryLookup: articleEntryLookup,
        root: article,
        pageContext: { url: requestUrl },
      }),
    );
    return Object.fromEntries(hydratedEntries);
  } catch (e) {
    globalObservability.getSentryClient().captureException?.(e, { data: { slug } });
    return {};
  }
}

/*
  Mixed-depth fetch strategy for articles:

  To support deep navigation (4 levels) while avoiding oversized responses,
  we fetch metadata and body content separately:
  1. Metadata + navigation (include=4) for full MegaNav structure
  2. Body content (include=1) to keep response size manageable

  This prevents "Invalid string length" errors on articles with many embedded assets
  while maintaining full navigation functionality.
*/
export async function fetchArticleBySlug({
  slug,
  requestUrl,
}: FetchBySlug): Promise<ArticleProps | null> {
  const client = createContentfulClient();

  const calculateReadingTime = (input: string) => {
    const WORDS_PER_MINUTE = 200;
    const matches = input.match(/(\w+)/g);
    if (!matches) {
      return null;
    }
    return matches.length / WORDS_PER_MINUTE;
  };

  const roundReadingTime = (time: number | null): number | null => {
    return time !== null ? Math.round(time) : null;
  };

  const [metadataEntry, bodyEntry] = await Promise.all([
    fetchArticleMetadata(client, slug),
    fetchArticleBody(client, slug),
  ]);

  if (!metadataEntry || !bodyEntry) {
    return null;
  }

  const { article, mergedIncludes } = mergeArticleData(metadataEntry, bodyEntry);

  article.fields.relatedArticles = await getRelatedArticles(article).catch(() => []);

  const readingTimeMinutes = roundReadingTime(
    calculateReadingTime(
      [
        article.fields.articleName,
        article.fields.headerSummary,
        article.fields.keyTakeaways && documentToPlainTextString(article.fields.keyTakeaways),
        documentToPlainTextString(article.fields.body),
      ]
        .filter(Boolean)
        .join(''),
    ),
  );

  const idToLoaderData = await hydrateArticleEntries(article, mergedIncludes, requestUrl, slug);

  // Convert undefined to null for Next.js serialization
  const fields = {
    ...article.fields,
    keyTakeaways: article.fields.keyTakeaways ?? null,
  };

  return {
    sys: {
      createdAt: article.sys.createdAt,
      updatedAt: article.sys.updatedAt,
      tags: article.metadata.tags?.map((item) => item?.sys?.id).filter(Boolean) || [],
    },
    fields,
    readingTimeMinutes,
    idToLoaderData,
  };
}

export async function fetchTopicPageBySlug({
  slug,
  pageNum,
  requestUrl,
}: FetchBySlug): Promise<TopicPageProps | null> {
  const client = createContentfulClient();
  const entries = await client.getEntries<ITopicPageFields>({
    content_type: 'topicPage',
    /*
      The field "include" specifies how many levels down of nested
      data that this fetch call should return. Please be aware that any
      value greater than 2 has significant impact on page performance.

      Setting to FETCH_DEEP to support MegaNav structure:
      TopicPage → header → mainNav → sections → links
    */
    include: FETCH_DEEP,
    'fields.slug': slug,
    limit: 1,
  });
  if (entries.items.length === 0) {
    return null;
  }
  const safeEntries = JSON.parse(entries.stringifySafe()) as Omit<
    typeof entries,
    'toPlainObject' | 'stringifySafe'
  >;
  const topicPage = safeEntries.items[0];
  let hydratedEntries: Array<[string, any]> = [];
  try {
    hydratedEntries = await Promise.all(
      hydrateEntries(
        {
          includes: safeEntries.includes?.Entry ?? [],
          entryLookup: topicPageEntryLookup,
          root: topicPage,
          pageContext: { pageNum, url: requestUrl },
        },
        // transform to new options based
      ),
    );
  } catch (e) {
    globalObservability.getSentryClient().captureException?.(e, {
      data: {
        slug,
      },
    });
  }
  const idToLoaderData = Object.fromEntries(hydratedEntries);

  return {
    sys: {
      createdAt: topicPage.sys.createdAt,
      updatedAt: topicPage.sys.updatedAt,
      locale: topicPage.sys.locale,
    },
    fields: topicPage.fields,
    idToLoaderData,
  };
}

async function getRelatedArticles(article: IArticle): Promise<Array<IArticle>> {
  // grab the article that we want to look up relatedArticles with specifically
  const articleWithRelated = (
    await fetchEntriesById<Pick<IArticleFields, 'relatedArticles'>>('article', [article.sys.id], {
      select: 'fields.relatedArticles',
      include: 0,
    })
  )[0];

  return (
    await fetchEntriesById<Pick<IArticleFields, 'slug' | 'authors' | 'articleName'>>(
      'article',
      (articleWithRelated?.fields?.relatedArticles || []).map((a) => a.sys.id),
      { select: 'fields.slug,fields.authors,fields.articleName' },
    )
  ).filter((article) => !!article) as Array<IArticle>;
}

export async function getCashOfferModal() {
  try {
    const cashOfferModal = await fetchEntryById<ILpComponentGetCashOfferModalFields>(
      'lpComponentGetCashOfferModal',
      '29FQNyPKP58Niqf5jcSM58',
      {
        include: FETCH_DEEP,
      },
    );
    return cashOfferModal;
  } catch (e) {
    // Capture the exception using the Sentry client
    globalObservability.getSentryClient().captureException?.(e, {
      data: {
        entryId: '29FQNyPKP58Niqf5jcSM58',
      },
    });
  }
  return undefined;
}

export async function fetchLandingPageV2BySlug({
  slug,
  pageNum,
  requestUrl,
  locale,
}: FetchBySlug): Promise<LandingPageV2Props | null> {
  const client = createContentfulClient();
  const entry = await client.getEntries<ILandingPageV2Fields>({
    content_type: 'landingPageV2',
    /*
      The field "include" specifies how many levels down of nested
      data that this fetch call should return. Please be aware that any
      value greater than 2 may have a significant impact on page performance,
      so it should be used sparingly and content authors should understand the
      impact of overusing components with several levels of nested data.

      Setting to FETCH_DEEP to support MegaNav structure:
      LandingPage → header → mainNav → sections → links
    */
    include: FETCH_DEEP,
    'fields.slug': slug,
    limit: 1,
    locale,
  });

  if (entry.items.length === 0) {
    return null;
  }

  const safeEntry = JSON.parse(entry.stringifySafe()) as Omit<
    typeof entry,
    'toPlainObject' | 'stringifySafe'
  >;
  const page = safeEntry.items[0] as ILandingPageV2;

  let hydratedEntries: Array<[string, any]> = [];
  try {
    hydratedEntries = await Promise.all(
      hydrateEntries({
        includes: safeEntry.includes?.Entry ?? [],
        entryLookup: landingPageV2EntryLookup,
        root: page,
        pageContext: {
          url: requestUrl,
          pageNum,
        },
      }),
    );
  } catch (e) {
    globalObservability.getSentryClient().captureException?.(e, {
      data: {
        slug,
      },
    });
  }
  const idToLoaderData = Object.fromEntries(hydratedEntries);
  return {
    fields: page.fields,
    idToLoaderData,
    markets: [], // gets populated by the landing page component
    public: page.fields.public,
  };
}

/**
 * Type guard: Check if item is a SimpleList (has sections) or just a Link
 */
function isSimpleList(item: ILpElementLink | ILpElementSimpleList): item is ILpElementSimpleList {
  return item?.fields != null && 'title' in item.fields && 'links' in item.fields;
}

/**
 * Transforms a simple link (ILpElementLink) to { name, href }
 */
function transformSimpleLink(link: ILpElementLink): { name: string; href: string } {
  return {
    name: link.fields?.text || '',
    href: link.fields?.url || '',
  };
}

/**
 * Transforms a section (ILpElementSimpleList with nested ILpElementLink items)
 */
function transformSection(section: ILpElementSimpleList): {
  title: string;
  links: Array<{ name: string; href: string }>;
} {
  const links =
    section.fields.links
      ?.filter((link) => link != null)
      .map((link) => transformSimpleLink(link as ILpElementLink)) || [];

  return {
    title: section.fields.title || '',
    links,
  };
}

/**
 * Transforms ISiteHeader mainNav into MegaNavItem[] format
 *
 * Two patterns:
 * 1. ILpElementLink → Simple nav item (e.g., "Agents")
 * 2. ILpElementSimpleList → Nav with dropdown containing sections (each section is also ILpElementSimpleList)
 */
export function transformHeaderNavToMegaNav(
  mainNav?: (ILpElementLink | ILpElementSimpleList)[] | undefined,
): MegaNavItem[] {
  if (!mainNav?.length) return [];

  return mainNav
    .filter((item) => item?.fields)
    .map((navItem): MegaNavItem | null => {
      // Pattern 1: Simple link without dropdown
      if (!isSimpleList(navItem)) {
        return {
          ...transformSimpleLink(navItem),
          sections: undefined,
        };
      }

      // Pattern 2: Nav item with dropdown sections
      const sections = (navItem.fields?.links || [])
        .filter((link) => link && isSimpleList(link))
        .map((section) => transformSection(section as ILpElementSimpleList));

      if (sections.length === 0) return null;

      return {
        name: navItem.fields?.title || '',
        href: sections[0]?.links[0]?.href || '#',
        sections,
      };
    })
    .filter((item): item is MegaNavItem => item !== null);
}

// Default Contentful entry IDs for global header/footer
const DEFAULT_HEADER_ENTRY_ID = '17QZKVW8LvgeQ2PY4kMoxC'; // "Default - Global Nav"
const DEFAULT_FOOTER_ENTRY_ID = '7xQR3lEo4pIkwZx2RdRqFC'; // "Default SiteFooter"

/**
 * Fetches the main site header by its Contentful entry ID.
 * This allows non-CMS pages (like marketplace) to use the same header/meganav.
 *
 * Uses FETCH_DEEP (4) to get nested nav structure:
 * header → mainNav → sections → links
 */
export async function fetchMainSiteHeader(): Promise<ISiteHeader | null> {
  try {
    const header = await fetchEntryById<ISiteHeader['fields']>(
      'siteHeader',
      DEFAULT_HEADER_ENTRY_ID,
      { include: FETCH_DEEP },
    );
    return header as ISiteHeader | null;
  } catch (error) {
    globalObservability.getSentryClient().captureException?.(error, {
      data: { entryId: DEFAULT_HEADER_ENTRY_ID },
    });
    return null;
  }
}

/**
 * Fetches the main site footer by its Contentful entry ID.
 * This allows non-CMS pages (like marketplace) to use the same footer.
 *
 * Uses FETCH_DEEP (4) to get nested nav structure:
 * footer → footerNav → links
 */
export async function fetchMainSiteFooter(): Promise<ISiteFooter | null> {
  try {
    const footer = await fetchEntryById<ISiteFooter['fields']>(
      'siteFooter',
      DEFAULT_FOOTER_ENTRY_ID,
      { include: FETCH_DEEP },
    );
    return footer as ISiteFooter | null;
  } catch (error) {
    globalObservability.getSentryClient().captureException?.(error, {
      data: { entryId: DEFAULT_FOOTER_ENTRY_ID },
    });
    return null;
  }
}

/**
 * Fetches both the main site header and footer by their Contentful entry IDs.
 * More efficient than calling fetchMainSiteHeader and fetchMainSiteFooter separately.
 */
export async function fetchMainSiteHeaderAndFooter(): Promise<{
  header: ISiteHeader | null;
  footer: ISiteFooter | null;
}> {
  try {
    const [header, footer] = await Promise.all([
      fetchEntryById<ISiteHeader['fields']>('siteHeader', DEFAULT_HEADER_ENTRY_ID, {
        include: FETCH_DEEP,
      }),
      fetchEntryById<ISiteFooter['fields']>('siteFooter', DEFAULT_FOOTER_ENTRY_ID, {
        include: FETCH_DEEP,
      }),
    ]);

    return {
      header: (header as ISiteHeader) ?? null,
      footer: (footer as ISiteFooter) ?? null,
    };
  } catch (error) {
    globalObservability.getSentryClient().captureException?.(error, {
      data: { headerEntryId: DEFAULT_HEADER_ENTRY_ID, footerEntryId: DEFAULT_FOOTER_ENTRY_ID },
    });
    return { header: null, footer: null };
  }
}
